import {MonthlyOfficeVisitReport} from "../../api/VisitWithStatStore";
import {CsvModel} from "../../util/CSVWriteUtil";
import {isoDate, plusDays, WeekDay, WeekDayFullNames, weekNumber, withDayOfWeek} from "../../util/DateUtil";
import {stringCmp} from "../../util/SortUtil";
import {getOrSet} from "../../util/MapUtil";

const weekDays = [
    WeekDay.Monday,
    WeekDay.Tuesday,
    WeekDay.Wednesday,
    WeekDay.Thursday,
    WeekDay.Friday,
    WeekDay.Saturday,
    WeekDay.Sunday,
];

export type WeeklyVisitReportRow = {
    week: string;
    from: string,
    to: string,
} & Record<WeekDay, number>;

type ReportWithOfficeVisits = Pick<MonthlyOfficeVisitReport, 'officeVisits'>;

export function weeklyVisitReportRows(reports: ReportWithOfficeVisits[]): WeeklyVisitReportRow[] {
    const weeks = new Map<string, WeeklyVisitReportRow>();

    reports.forEach(report => {
        report.officeVisits.forEach(visit => {
            const date = new Date(visit.date);

            const monday = withDayOfWeek(date, WeekDay.Monday);
            const weekYear = plusDays(monday, 3).getFullYear();
            const week = weekNumber(date).toString().padStart(2, '0');
            const weekLabel = `${weekYear}W${week}`;
            const row = getOrSet(weeks, weekLabel, () => {
                return {
                    week: weekLabel,
                    from: isoDate(monday),
                    to: isoDate(withDayOfWeek(date, WeekDay.Sunday)),
                    [WeekDay.Sunday]: 0,
                    [WeekDay.Monday]: 0,
                    [WeekDay.Tuesday]: 0,
                    [WeekDay.Wednesday]: 0,
                    [WeekDay.Thursday]: 0,
                    [WeekDay.Friday]: 0,
                    [WeekDay.Saturday]: 0,
                }
            });

            const dayOfWeek = date.getDay() as WeekDay;
            row[dayOfWeek] += visit.visitorCount;
        });
    });

    return Array.from(weeks.values())
        .sort(stringCmp(it => it.week));
}

export function downloadWeeklyVisitReportCsv(reports: ReportWithOfficeVisits[]) {
    const model = new CsvModel<WeeklyVisitReportRow>([
        {
            header: 'Week',
            value: row => row.week,
        },
        {
            header: 'From',
            value: row => row.from,
        },
        {
            header: 'To',
            value: row => row.to,
        },
        ...weekDays.map(day => ({
            header: WeekDayFullNames[day],
            value: (row: WeeklyVisitReportRow) => row[day].toString(),
        })),
    ]);

    model.downloadCsv(
        `weekly_visit_report_${new Date().toISOString()}.csv`,
        weeklyVisitReportRows(reports),
    );
}
